import type {
  LoginAsRequest,
  UserJwtResponse,
  UserProfileResponseRecord,
} from '@on3/api';
import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { cmsUrl, externalApi } from '@on3/ui-lib/utils/api';
import { On3Avatar } from 'components/Image/On3Avatar';
import { Twitter } from 'components/Svg/Twitter';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { SidebarContainer } from 'components/UI/Layout/Containers/Sidebar/SidebarContainer/SidebarContainer';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import parse from 'html-react-parser';
import { useRouter } from 'next/router';
import { type MouseEventHandler, useCallback } from 'react';

import styles from './AuthorBio.module.scss';

interface AuthorBioProps {
  // this is returned as key. We need to get the api to return this as userKey
  author?: UserProfileResponseRecord;
}

export const AuthorBio = ({ author }: AuthorBioProps) => {
  const { name, bio, jobTitle, twitterHandle, profilePicture, key } =
    author || {};
  const { user, setUserToken } = useAuth();
  const router = useRouter();
  const parsedDom = parse(bio || '');

  const handleLogInAs: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      if (key) {
        const request: LoginAsRequest = {
          userKey: key,
        };

        externalApi
          .post('/users/v1/users/login-as', request)
          .then(({ data }: { data: UserJwtResponse }) => {
            setUserToken(data);
            router.push('/my/settings/');
          })
          .catch((e) => {
            alert(e.message);
          });
      }
    }, [key, setUserToken, router]);

  return (
    <section>
      {(user?.rg?.includes('administrator') ||
        user?.rg?.includes('cs_admin')) && (
        <PrimaryButton onClick={handleLogInAs} variant="text">
          LogInAs
        </PrimaryButton>
      )}
      {user?.rg?.includes('administrator') && (
        <a
          href={`${cmsUrl}/wp-admin/user-edit.php?user_id=${key}`}
          rel="noreferrer"
          target="_blank"
        >
          Edit User
        </a>
      )}
      <Text className={styles.authorName} variant="h1">
        {name}
      </Text>
      <SidebarContainer title={`${jobTitle ?? name}`}>
        <div className={styles.bioContainer}>
          {/* TODO: We need BE to populate profilePictureResponse */}
          <On3Avatar
            altText={`${name} Avatar`}
            className={styles.avatar}
            radiusFive
            source={profilePicture?.url}
          />
          <div className={styles.bioVerbiage}>
            <Text>{parsedDom}</Text>
            {twitterHandle && (
              <p className={styles.twitterHandle}>
                <Twitter />

                <TextLink
                  href={`https://twitter.com/${twitterHandle}`}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="caption"
                >
                  @{twitterHandle}
                </TextLink>
              </p>
            )}
          </div>
        </div>
      </SidebarContainer>
    </section>
  );
};
